// Operator › Skill proposals (`/operator/skill-proposals`, one at `/{company}/{id}`;
// SKILL-PROPOSALS-OPERATOR, Andrei 2026-11-01): the skills digital employees proposed through the
// one door (`propose_skill`), across every granted company. Platform super admins only: the API
// answers 404 to anyone else, and no company page shows a proposal, its draft or its reviewer.
// Skillsmith reviews each one first (a recommendation, a note, maybe a revised draft: `Ready`);
// the super admin approves or rejects here, the only human gate. Approving installs nothing: it
// files a work item for the repo pipeline; Mark shipped once the repo landed it. The DE is told
// only "accepted" or "declined".
//
// NIGHTLY-REVIEW (Andrei, 2026-11-01): a proposal may come from the DE's nightly self-review
// (`source = nightly_review`, with what changes in outcome terms and the KPI effect it expects).
// Before a change reaches a client's agents, the "Client conversation" step: the operator drafts an
// outcome-terms message to the client owner ("your Marketing Director will now ask for the
// audience before drafting"; never skill internals, rule 7m), edits it here and sends it by hand;
// then records the client's OK, or why no consent is needed. Mark shipped is held until then.
// Calls on load: `operator.skillProposals.list` (`?status`), or `.get` for the detail.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  InfoTip,
  LocationHeader,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { LightbulbIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { operatorSkillProposalQuery, operatorSkillProposalsQuery, opsKeys } from "../ops-queries";
import { Facts, LoadError, Loading, Section } from "../screen-parts";

export type OperatorSkillProposal = OperationOutput<"operator.skillProposals.get">;
type Status = OperatorSkillProposal["status"];

export const SKILL_PROPOSAL_STATUS_LABELS: Record<Status, string> = {
  proposed: "Waiting for Skillsmith",
  ready: "Ready for you",
  approved: "Approved · in the repo queue",
  rejected: "Rejected",
  shipped: "Shipped",
};

const STATUS_TONE: Record<Status, "warning" | "info" | "neutral" | "success"> = {
  proposed: "neutral",
  ready: "warning",
  approved: "info",
  rejected: "neutral",
  shipped: "success",
};

type ConsentState = OperatorSkillProposal["consent"]["state"];
export const CONSENT_LABELS: Record<ConsentState, string> = {
  needs_client_ok: "Waiting for the client's OK",
  client_ok: "Client agreed",
  no_consent_needed: "No consent needed",
};
const CONSENT_TONE: Record<ConsentState, "warning" | "success" | "neutral"> = {
  needs_client_ok: "warning",
  client_ok: "success",
  no_consent_needed: "neutral",
};

const FILTERS = [
  ["ready", "Ready"],
  ["proposed", "With Skillsmith"],
  ["approved", "Approved"],
  ["shipped", "Shipped"],
  ["rejected", "Rejected"],
  ["all", "All"],
] as const;

const ABOUT =
  "Digital employees never write their own skills: they propose them, with why and the runs " +
  "behind it. Skillsmith reviews each one and recommends; you decide. Approving sends it to the " +
  "repo, where it is versioned, checked and shipped. Companies never see any of this, and the " +
  "digital employee is told only that its suggestion was accepted or declined.";

const invalidate = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: opsKeys.skillProposals() });

export function OperatorSkillProposalsPage({ api }: { api: Api }) {
  const [status, setStatus] = useState<(typeof FILTERS)[number][0]>("ready");
  const q = useQuery(operatorSkillProposalsQuery(api, status));
  const rows = q.data?.data ?? [];
  return (
    <div className="flex min-w-0 flex-col" data-slot="operator-skill-proposals">
      <LocationHeader
        icon={LightbulbIcon}
        title="Skill proposals"
        badge={<InfoTip label="About skill proposals">{ABOUT}</InfoTip>}
      />
      <Toolbar bleed aria-label="Skill proposal filters">
        <ToggleGroup
          variant="chip"
          value={[status]}
          onValueChange={(v: unknown[]) => {
            const next = v[0] as (typeof FILTERS)[number][0] | undefined;
            if (next) setStatus(next);
          }}
          aria-label="Show"
        >
          {FILTERS.map(([value, label]) => (
            <ToggleGroupItem key={value} value={value}>
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Toolbar>
      <div className="flex min-w-0 flex-col px-6 pt-3 max-md:px-4">
        {q.isPending ? (
          <Loading label="Loading skill proposals" rows={3} />
        ) : q.isError ? (
          <LoadError what="Skill proposals" error={q.error} />
        ) : !rows.length ? (
          <EmptyState
            variant="card"
            icon={LightbulbIcon}
            title={status === "ready" ? "Nothing ready for you" : "No skill proposals"}
            description="When a digital employee proposes a skill, Skillsmith reviews it and it shows up here."
          />
        ) : (
          <Table data-slot="operator-skill-proposals-table">
            <TableCaption>Skill proposals across your companies, newest first.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Skill</TableHead>
                <TableHead scope="col">Company</TableHead>
                <TableHead scope="col">Proposed by</TableHead>
                <TableHead scope="col">Skillsmith</TableHead>
                <TableHead scope="col">Proposed</TableHead>
                <TableHead scope="col">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow
                  key={`${p.company.id}:${p.id}`}
                  data-proposal={p.id}
                  data-status={p.status}
                >
                  <TableHead scope="row" className="font-medium whitespace-normal">
                    <span className="flex flex-col gap-1">
                      <Link
                        to="/operator/skill-proposals/$companyId/$proposalId"
                        params={{ companyId: p.company.id, proposalId: p.id }}
                        className="font-mono text-primary-text underline-offset-4 hover:underline"
                      >
                        {p.name}
                      </Link>
                      <span className="line-clamp-2 text-sm font-normal text-muted-foreground">
                        {p.outcome ?? p.why}
                      </span>
                    </span>
                  </TableHead>
                  <TableCell>{p.company.name}</TableCell>
                  <TableCell>
                    <span className="flex flex-col gap-1">
                      {p.proposer.name}
                      {p.source === "nightly_review" ? (
                        <Badge variant="neutral">Nightly review</Badge>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell>
                    {p.review ? (
                      <Badge
                        variant={p.review.recommendation === "approve" ? "success" : "neutral"}
                      >
                        {p.review.recommendation === "approve" ? "Approve" : "Reject"}
                        {p.review.revised ? " · revised" : ""}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">Not yet</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {formatDateTime(p.created_at)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_TONE[p.status]}>
                      {SKILL_PROPOSAL_STATUS_LABELS[p.status]}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

/** Approve or reject, with an optional note (kept operator-side: the DE never sees it). */
function DecideForm({ api, proposal: p }: { api: Api; proposal: OperatorSkillProposal }) {
  const qc = useQueryClient();
  const noteId = useId();
  const [note, setNote] = useState("");
  const decide = useMutation({
    mutationFn: (decision: "approve" | "reject") =>
      api.client.operator.skillProposals.decide({
        params: { company_id: p.company.id, proposal_id: p.id },
        body: { decision, ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSettled: () => invalidate(qc),
  });
  return (
    <div className="flex max-w-2xl flex-col gap-3" data-slot="skill-proposal-decide">
      <label htmlFor={noteId} className="text-sm font-medium">
        Note (optional; only operators see it)
      </label>
      <Textarea
        id={noteId}
        value={note}
        rows={3}
        maxLength={2000}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Button disabled={decide.isPending} onClick={() => decide.mutate("approve")}>
          Approve
        </Button>
        <Button
          variant="outline"
          disabled={decide.isPending}
          onClick={() => decide.mutate("reject")}
        >
          Reject
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Approving installs nothing: it goes to the repo queue, and the skill reaches agents once it
        ships. The digital employee is told only accepted or declined.
      </p>
    </div>
  );
}

function ShipButton({ api, proposal: p }: { api: Api; proposal: OperatorSkillProposal }) {
  const qc = useQueryClient();
  const ship = useMutation({
    mutationFn: () =>
      api.client.operator.skillProposals.ship({
        params: { company_id: p.company.id, proposal_id: p.id },
        body: {},
      }),
    onSettled: () => invalidate(qc),
  });
  // NIGHTLY-REVIEW: held until the client conversation is settled (the API refuses it too).
  const held = p.consent.blocks_shipping;
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={ship.isPending || held}
      title={held ? "Settle the client conversation first" : undefined}
      onClick={() => ship.mutate()}
    >
      Mark shipped
    </Button>
  );
}

/**
 * NIGHTLY-REVIEW: the client conversation. Draft the outcome-terms message (saved on the
 * proposal), edit it, send it by hand; then record the client's OK, or why no consent is needed.
 * Nothing here quotes the skill, the draft or the reviewer.
 */
function ClientConversation({ api, proposal: p }: { api: Api; proposal: OperatorSkillProposal }) {
  const qc = useQueryClient();
  const subjectId = useId();
  const bodyId = useId();
  const reasonId = useId();
  const params = { company_id: p.company.id, proposal_id: p.id };
  const [subject, setSubject] = useState(`A small change to how ${p.proposer.name} works`);
  const [body, setBody] = useState(p.consent.message_draft ?? "");
  const [to, setTo] = useState<{ membership_id: string; name: string; email: string | null }[]>([]);
  const [reason, setReason] = useState(p.consent.reason ?? "");
  const draft = useMutation({
    mutationFn: () => api.client.operator.skillProposals.clientMessage.draft({ params, body: {} }),
    onSuccess: (d) => {
      setSubject(d.subject);
      setBody(d.body);
      setTo(d.to);
    },
    onSettled: () => invalidate(qc),
  });
  const send = useMutation({
    mutationFn: () =>
      api.client.operator.skillProposals.clientMessage.send({
        params,
        body: { subject, body, to: to.filter((x) => x.email).map((x) => x.membership_id) },
      }),
    onSettled: () => invalidate(qc),
  });
  const consent = useMutation({
    mutationFn: (state: ConsentState) =>
      api.client.operator.skillProposals.consent({
        params,
        body: { state, ...(reason.trim() ? { reason: reason.trim() } : {}) },
      }),
    onSettled: () => invalidate(qc),
  });
  const closed = p.status === "rejected" || p.status === "shipped";
  return (
    <div className="flex max-w-2xl flex-col gap-4" data-slot="skill-proposal-client-conversation">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={CONSENT_TONE[p.consent.state]}>{CONSENT_LABELS[p.consent.state]}</Badge>
        {p.consent.message_sent_at ? (
          <span className="text-sm text-muted-foreground">
            Message sent {formatDateTime(p.consent.message_sent_at)}
          </span>
        ) : null}
        {p.consent.reason ? (
          <span className="text-sm text-muted-foreground">· {p.consent.reason}</span>
        ) : null}
      </div>
      {!closed && p.consent.state !== "no_consent_needed" ? (
        <div className="flex flex-col gap-3" data-slot="skill-proposal-client-message">
          <p className="text-sm text-muted-foreground">
            Tell the client owner what will change, in outcome terms. The draft never names the
            skill or quotes it; edit it, then send. Nothing is sent until you press Send.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={draft.isPending}
              onClick={() => draft.mutate()}
            >
              Draft message to client
            </Button>
          </div>
          {body ? (
            <>
              <label htmlFor={subjectId} className="text-sm font-medium">
                Subject
              </label>
              <Textarea
                id={subjectId}
                value={subject}
                rows={1}
                maxLength={200}
                onChange={(e) => setSubject(e.target.value)}
              />
              <label htmlFor={bodyId} className="text-sm font-medium">
                Message
              </label>
              <Textarea
                id={bodyId}
                value={body}
                rows={10}
                maxLength={8000}
                onChange={(e) => setBody(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                To:{" "}
                {to.length === 0
                  ? "draft first to load the client owners"
                  : to.map((x) => `${x.name}${x.email ? "" : " (no address)"}`).join(", ")}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={
                    send.isPending || !subject.trim() || !body.trim() || !to.some((x) => x.email)
                  }
                  onClick={() => send.mutate()}
                >
                  Send to client
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      {!closed ? (
        <div className="flex flex-col gap-3" data-slot="skill-proposal-consent">
          <label htmlFor={reasonId} className="text-sm font-medium">
            What the client said, or why no consent is needed
          </label>
          <Textarea
            id={reasonId}
            value={reason}
            rows={2}
            maxLength={2000}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={consent.isPending || p.consent.state === "client_ok"}
              onClick={() => consent.mutate("client_ok")}
            >
              Client agreed
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={
                consent.isPending || p.consent.state === "no_consent_needed" || !reason.trim()
              }
              title={reason.trim() ? undefined : "Say why first"}
              onClick={() => consent.mutate("no_consent_needed")}
            >
              No consent needed
            </Button>
            {p.consent.state !== "needs_client_ok" ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={consent.isPending}
                onClick={() => consent.mutate("needs_client_ok")}
              >
                Reopen
              </Button>
            ) : null}
          </div>
          {p.consent.blocks_shipping ? (
            <p className="text-sm text-muted-foreground">
              Mark shipped stays off until the client agreed or you recorded why no consent is
              needed (a bug fix, an internal-only employee).
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Draft({ text, slot }: { text: string; slot: string }) {
  return (
    <pre
      className="max-h-[32rem] overflow-auto rounded-md border bg-muted/40 p-4 font-mono text-xs whitespace-pre-wrap"
      data-slot={slot}
    >
      {text}
    </pre>
  );
}

export function OperatorSkillProposalPage({
  api,
  companyId,
  proposalId,
}: {
  api: Api;
  companyId: string;
  proposalId: string;
}) {
  const q = useQuery(operatorSkillProposalQuery(api, companyId, proposalId));
  const p = q.data;
  return (
    <div className="flex min-w-0 flex-col" data-slot="operator-skill-proposal">
      <LocationHeader
        icon={LightbulbIcon}
        title={p ? p.name : "Skill proposal"}
        {...(p ? { subtitle: `${p.company.name} · proposed by ${p.proposer.name}` } : {})}
        badge={
          p ? (
            <Badge variant={STATUS_TONE[p.status]}>{SKILL_PROPOSAL_STATUS_LABELS[p.status]}</Badge>
          ) : null
        }
        actions={p && p.status === "approved" ? <ShipButton api={api} proposal={p} /> : null}
      />
      {q.isPending ? (
        <Loading label="Loading the skill proposal" rows={4} />
      ) : q.isError ? (
        <LoadError what="The skill proposal" error={q.error} />
      ) : p ? (
        <>
          <Section id="skill-proposal-facts" title="Proposal">
            <Facts
              items={[
                { label: "Skill", value: <span className="font-mono">{p.name}</span> },
                { label: "Company", value: p.company.name },
                {
                  label: "Proposed by",
                  value:
                    p.source === "nightly_review"
                      ? `${p.proposer.name} · nightly review`
                      : p.proposer.name,
                },
                { label: "Proposed", value: formatDateTime(p.created_at) },
                ...(p.expected_effect
                  ? [{ label: "Expected effect", value: p.expected_effect }]
                  : []),
                {
                  label: "Evidence",
                  value: (
                    <span className="flex flex-wrap gap-2 font-mono text-xs">
                      {p.evidence.map((e) => (
                        <span key={`${e.kind}:${e.id}`}>
                          {e.kind} {e.id.slice(-8)}
                        </span>
                      ))}
                    </span>
                  ),
                },
                ...(p.decided_at
                  ? [
                      {
                        label: p.status === "rejected" ? "Rejected" : "Approved",
                        value: formatDateTime(p.decided_at),
                      },
                    ]
                  : []),
                ...(p.work_item_id
                  ? [
                      {
                        label: "Repo work item",
                        value: <span className="font-mono text-xs">{p.work_item_id}</span>,
                      },
                    ]
                  : []),
                ...(p.shipped_at
                  ? [{ label: "Shipped", value: formatDateTime(p.shipped_at) }]
                  : []),
              ]}
            />
          </Section>
          {p.outcome ? (
            <Section
              id="skill-proposal-outcome"
              title="What changes, in outcome terms"
              description="What the client may be told. Never the skill's name or text."
            >
              <p className="max-w-3xl text-sm whitespace-pre-wrap">{p.outcome}</p>
            </Section>
          ) : null}
          <Section id="skill-proposal-why" title="Why">
            <p className="max-w-3xl text-sm whitespace-pre-wrap">{p.why}</p>
          </Section>
          <Section
            id="skill-proposal-client"
            title="Client conversation"
            description="Before a change reaches the client's agents: outcomes, never skill internals."
          >
            <ClientConversation api={api} proposal={p} />
          </Section>
          {p.review ? (
            <Section
              id="skill-proposal-review"
              title="Skillsmith's review"
              description={`Recommends: ${p.review.recommendation === "approve" ? "approve" : "reject"} · ${formatDateTime(p.review.reviewed_at)}`}
            >
              {p.review.note ? (
                <p className="max-w-3xl text-sm whitespace-pre-wrap">{p.review.note}</p>
              ) : null}
              {p.review.draft ? (
                <Draft text={p.review.draft} slot="skill-proposal-revised-draft" />
              ) : null}
            </Section>
          ) : null}
          {p.decision_note ? (
            <Section id="skill-proposal-notes" title="Decision note">
              <p className="max-w-3xl text-sm whitespace-pre-wrap">{p.decision_note}</p>
            </Section>
          ) : null}
          <Section
            id="skill-proposal-draft"
            title="Draft SKILL.md"
            description="As the digital employee wrote it. Text only: nothing in it runs."
          >
            {p.draft ? <Draft text={p.draft} slot="skill-proposal-draft" /> : null}
          </Section>
          {p.status === "proposed" || p.status === "ready" ? (
            <Section id="skill-proposal-decide" title="Your decision">
              <DecideForm api={api} proposal={p} />
            </Section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
